// domain/services/targetProgress.js
import { isExcludedFromProgressCount, TARGET_DATA_TYPE_META } from '../constants/targetDataType';
import { TARGET_STATUS, TARGET_STATUS_ORDER } from '../constants/targetStatus';

/**
 * How many of a program's targets sit in each status.
 *
 * A PROJECTION, NOT AN INVARIANT. It is stored on the program document so the program list does not
 * have to read every target, because Firestore cannot count without reading. That makes it a cache,
 * and a cache is allowed to be briefly stale — which is exactly why Program and Target are two
 * aggregates rather than one.
 *
 * A projection is allowed one writer, and there were six computing it differently, so the number on
 * a program card depended on which of them last touched it. This is the frontend's one:
 * `mixins/Targets.js` delegates here, and both importers went with the old one.
 *
 * `functions/utils/targets.js` is the backend's, and `installStorePackage` calls it rather than
 * keeping a copy. STILL DIVERGENT: `scripts/mergePrograms.js` computes its own and writes it. It is
 * a one-off script rather than a running code path, which is the only reason it was left.
 */

/** A target's status lives in its lifecycle value object; this is the only place that matters. */
const statusOf = (target) => target?.lifecycle?.status;

/** Sub-target data types whose parents absorb them into a single count. */
const PARENT_ABSORBS_SUB_TARGETS = Object.entries(TARGET_DATA_TYPE_META)
  .filter(([, meta]) => meta.excludesSubTargetsFromProgressCount === true)
  .map(([dataType]) => dataType);

/** Data types that may hold children at all; anything parented elsewhere is an orphan. */
const SUB_TARGET_CAPABLE = Object.entries(TARGET_DATA_TYPE_META)
  .filter(([, meta]) => meta.hasSubTargets === true)
  .map(([dataType]) => dataType);

/**
 * Count a program's targets by status.
 *
 * EVERY STATUS APPEARS, including the ones at zero. A caller reading a missing key as zero and a
 * caller reading it as unknown are both reasonable, and the disagreement is invisible until a
 * program card and a progress report differ about the same program.
 *
 * ANY STATUS THAT IS NOT ONE OF THE SEVEN COUNTS AS `acquisition`. Workflows may define custom
 * statuses, and all of them are currently acquisition-type. This is the behaviour of the function
 * being replaced, kept deliberately: it is wrong the day a workflow defines a custom status that is
 * not an acquisition, and that day needs the workflows loaded here to fix properly.
 *
 * AN ORPHAN SUB-TARGET IS NOT COUNTED. Changing a parent's data type to one that cannot hold
 * children leaves the children behind, pointing at a parent with nowhere to put them. They are
 * invisible in the program and counting them made the card disagree with what the screen showed.
 *
 * @param {Array<import('../model/Target.types').Target>} programTargets - every target of one
 *   program, sub-targets included. Already mapped: a caller holding stored documents runs them
 *   through `toTarget` first, which is also where the editor's staged-edit markers stop.
 * @returns {Array<{ status: string, count: number }>} one entry per built-in status
 */
export function countTargetsPerStatus(programTargets) {
  const targets = Array.isArray(programTargets) ? programTargets : [];

  const absorbingParentIds = new Set(
    targets
      .filter((target) => PARENT_ABSORBS_SUB_TARGETS.includes(target?.dataType))
      .map((target) => target?.id)
      .filter(Boolean),
  );

  // Only parents that are actually present can be judged. A target whose parent is not in the list
  // is counted rather than assumed orphaned, because the caller may simply have passed a subset.
  const orphanParentIds = new Set(
    targets
      .filter((target) => !SUB_TARGET_CAPABLE.includes(target?.dataType))
      .map((target) => target?.id)
      .filter(Boolean),
  );

  const countable = targets.filter((target) => (
    !absorbingParentIds.has(target?.parentTargetId)
    && !orphanParentIds.has(target?.parentTargetId)
    && !isExcludedFromProgressCount(target)
  ));

  const builtInStatuses = new Set(TARGET_STATUS_ORDER);

  return TARGET_STATUS_ORDER.map((status) => ({
    status,
    count: countable.filter((target) => (
      status === TARGET_STATUS.ACQUISITION
        ? !builtInStatuses.has(statusOf(target)) || statusOf(target) === TARGET_STATUS.ACQUISITION
        : statusOf(target) === status
    )).length,
  }));
} // countTargetsPerStatus()

/**
 * How many targets a program has in a given status, read from a stored projection.
 *
 * The read side of the same concept, so a caller never has to know that a missing entry means zero.
 *
 * @param {Array<{ status: string, count: number }>|null|undefined} targetsPerStatus
 * @param {string} status
 * @returns {number}
 */
export function targetsInStatus(targetsPerStatus, status) {
  if (!Array.isArray(targetsPerStatus)) {
    return 0;
  }
  return targetsPerStatus.find((entry) => entry?.status === status)?.count ?? 0;
} // targetsInStatus()
